"use client";

import Image from "next/image";
import { motion } from "framer-motion";
import { siteConfig } from "@/config/site";
import { currentPhase, getCTAConfig } from "@/config/game-status";
import { useTranslation } from "@/i18n";
import { Button } from "@/components/ui/Button";
import { NewsletterInline } from "@/components/ui/NewsletterInline";
import { SteamLogo } from "@/components/ui/SteamLogo";

export function Hero() {
    const cta = getCTAConfig(currentPhase, siteConfig.steamUrl);
    const { t } = useTranslation();

    return (
        <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
            {/* Planetary video background - preload=none to avoid blocking 10MB on load */}
            <div className="absolute inset-0">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="none"
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover"
                >
                    <source src="/media/hero-background.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-void/70" />
                <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-void to-transparent" />
                <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-void/50 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center mx-auto max-w-5xl px-6 md:px-8 text-center w-full">
                {/* Badge */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-2"
                >
                    <span className="inline-block px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-mars border border-mars/30 rounded-full bg-void/50 backdrop-blur-sm">
                        {t.hero.badge}
                    </span>
                </motion.div>

                {/* Hero Logo - explicit width/height prevents layout shift */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    className="my-0 h-20 sm:h-24 md:h-32 lg:h-40 relative w-full"
                >
                    <h1 className="sr-only">Expedition 744 - Survive. Build. Transform Mars.</h1>
                    <Image
                        src="/media/library_text_logo.webp"
                        alt="Expedition 744"
                        fill
                        sizes="(max-width: 768px) 90vw, 70vw"
                        className="object-contain mx-auto"
                        priority
                    />
                </motion.div>

                {/* Tagline */}
                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="text-xl md:text-2xl lg:text-3xl font-semibold text-text-secondary leading-relaxed max-w-3xl mt-2 mb-8"
                >
                    {t.hero.subheadline}
                </motion.p>

                {/* CTAs */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <Button
                        variant="primary"
                        size="lg"
                        href={cta.primary.href}
                        external={cta.primary.external}
                    >
                        <SteamLogo
                            width={68}
                            height={17}
                            className="h-[17px] w-auto shrink-0 brightness-0 invert"
                        />
                        <span className="ml-2">{t.cta.wishlist}</span>
                    </Button>
                    <Button variant="secondary" size="lg" href="#trailer">
                        <span>▶</span>
                        <span className="ml-2">{t.hero.watchTrailer}</span>
                    </Button>
                </motion.div>

                {/* Newsletter inline CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-10 flex flex-col items-center gap-2"
                >
                    <p className="text-xs uppercase tracking-[0.14em] font-semibold text-text-muted mb-1">
                        {t.hero.newsletterLabel}
                    </p>
                    <NewsletterInline compact />
                    <p className="text-xs text-text-muted mt-1 opacity-70">
                        {t.hero.newsletterReassurance}
                    </p>
                </motion.div>
            </div>

            {/* Scroll indicator - CSS keyframe animation (compositor-only, no layout) */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 1.4 }}
                className="relative z-10 flex justify-center pb-8"
            >
                <a
                    href="#story"
                    className="flex flex-col items-center gap-2 text-text-muted hover:text-text-secondary transition-colors group"
                >
                    <span className="text-xs uppercase tracking-[0.15em]">
                        {t.hero.scrollLabel}
                    </span>
                    <span className="scroll-bounce-arrow text-text-muted group-hover:text-mars transition-colors">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M10 4v12m0 0l-4-4m4 4l4-4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                </a>
            </motion.div>
        </section>
    );
}
